import React from 'react';
import { Phone } from 'lucide-react';
import { formatPhoneDisplay, formatPhoneWithExtension, buildPhoneHref } from '../../utils/phoneUtils';

interface PhoneLinkProps {
  /** Phone number in E.164 format (e.g., "+15551234567") */
  phone: string | null | undefined;
  /** Optional phone extension */
  extension?: string | null;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the phone icon */
  showIcon?: boolean;
  /** Custom display text (overrides default formatting) */
  displayText?: string;
  /** Phone protocol to use (defaults to 'Tel') */
  protocol?: 'Tel' | 'Callto' | 'Sip' | 'Custom' | 'Disabled';
  /** Custom prefix for Custom protocol */
  customPrefix?: string;
}

/**
 * A phone number component that renders as a clickable link.
 * Falls back to plain text if protocol is Disabled.
 *
 * Note: This is a simplified version. The full version with PhoneProtocolContext
 * will be available once the settings infrastructure is migrated.
 */
const PhoneLink: React.FC<PhoneLinkProps> = ({
  phone,
  extension,
  className = '',
  showIcon = false,
  displayText,
  protocol = 'Tel',
  customPrefix,
}) => {
  // If no phone number, render nothing
  if (!phone) {
    return null;
  }

  // Format for display
  const formattedPhone = displayText
    ? displayText
    : extension
      ? formatPhoneWithExtension(phone, extension)
      : formatPhoneDisplay(phone);

  // If no formatted display, render nothing
  if (!formattedPhone) {
    return null;
  }

  // Build href based on protocol
  const href = buildPhoneHref(phone, protocol, customPrefix);

  // Base styling for the phone number
  const baseClasses = 'inline-flex items-center gap-1';

  // If disabled or no href, render as plain text
  if (protocol === 'Disabled' || !href) {
    return (
      <span className={`${baseClasses} ${className}`}>
        {showIcon && <Phone className="h-4 w-4 text-gray-400" />}
        {formattedPhone}
      </span>
    );
  }

  // Render as clickable link
  return (
    <a
      href={href}
      className={`${baseClasses} text-blue-600 hover:text-blue-800 hover:underline ${className}`}
    >
      {showIcon && <Phone className="h-4 w-4" />}
      {formattedPhone}
    </a>
  );
};

export default PhoneLink;

/**
 * A simple phone display component that just formats the phone number
 * without making it clickable. Useful for read-only displays or
 * when you want to handle clicks manually.
 */
export const PhoneDisplay: React.FC<{
  phone: string | null | undefined;
  extension?: string | null;
  className?: string;
}> = ({ phone, extension, className = '' }) => {
  if (!phone) return null;

  const formatted = extension
    ? formatPhoneWithExtension(phone, extension)
    : formatPhoneDisplay(phone);

  if (!formatted) return null;

  return <span className={className}>{formatted}</span>;
};
